import type { NodeDefinition } from "@/generated";
import type { NestedRefRule } from "@/types/api/spotflow/spotflow-config";
import type {
  NodeDefinitionV2,
  NodeV2Data,
  GraphNodeV2,
} from "@/types/spotflow-v2";
import type { DynamicNodeConfig } from "@/store/editor-v2";
import { resolveBindingTarget, type SecretLookupEntry } from "./mentionSource";

// remark-directive escapes the CommonMark punctuation set *and* the colon (a
// directive metacharacter) inside a `:var[...]` label, so a secret ref like
// `__secrets__.flow:<id>` serializes as `__secrets__.flow\:<id>`. Unescape all
// of them so the canonical ref matches the raw `<scope>:<id>` form.
const MARKDOWN_ESCAPE_IN_REF = /\\([\\`*_{}[\]()#+\-.!:])/g;

export function normalizeVariableRefId(ref: string): string {
  return ref.trim().replace(MARKDOWN_ESCAPE_IN_REF, "$1");
}

export const VAR_REF_PATTERN = /:var\[([^\]]+)\]/g;

export const WHOLE_VAR_REF_PATTERN = /^\s*:var\[([^\]]+)\]\s*$/;

export const INLINE_VAR_REF_PATTERN = /\(\(([^)]+)\)\)|:var\[([^\]]+)\]/g;

export function formatVarRefToken(refId: string): string {
  return `:var[${refId}]`;
}

// V1 nodes name their own variable inputs as ((name)); the engine fills them
// in by name at run time.
export const TEMPLATE_VAR_REF_PATTERN = /\(\(([^)]+)\)\)/g;

export function formatTemplateRefToken(name: string): string {
  return `((${name}))`;
}

export type VariableRefSyntax = "var" | "template";

export function formatRefToken(syntax: VariableRefSyntax, id: string): string {
  return syntax === "template"
    ? formatTemplateRefToken(id)
    : formatVarRefToken(id);
}

// Playbook-local template token ({{loop_item}}, {{loop_index}}, prior step
// outputs). Resolved by the playbook executor at run time, NOT by the engine —
// it must stay plain text in the stored value, never become a :var ref.
export const WHOLE_TEMPLATE_TOKEN_PATTERN = /^\{\{([^{}]+)\}\}$/;

export function isTemplateVariableToken(ref: string): boolean {
  return WHOLE_TEMPLATE_TOKEN_PATTERN.test(ref.trim());
}

export function parseTemplateVariableName(ref: string): string | null {
  const match = WHOLE_TEMPLATE_TOKEN_PATTERN.exec(ref.trim());
  return match?.[1] ?? null;
}

export function formatTemplateToken(name: string): string {
  return `{{${name}}}`;
}

const TEMPLATE_VARIABLE_LABELS: Record<string, string> = {
  loop_item: "Loop Item",
  loop_index: "Loop Index",
};

export function templateVariableLabel(name: string): string {
  return TEMPLATE_VARIABLE_LABELS[name] ?? name;
}

export function parseVarRefId(
  ref: string
): { sourceNodeId: string; outputName: string } | null {
  const normalized = normalizeVariableRefId(ref);
  const dotIdx = normalized.indexOf(".");
  if (dotIdx <= 0) return null;
  const sourceNodeId = normalized.slice(0, dotIdx);
  const outputName = normalized.slice(dotIdx + 1);
  if (!outputName) return null;
  return { sourceNodeId, outputName };
}

export function extractVarRefIdsFromString(text: string): string[] {
  const ids: string[] = [];
  const re = new RegExp(VAR_REF_PATTERN.source, "g");
  for (const match of text.matchAll(re)) {
    ids.push(normalizeVariableRefId(match[1]));
  }
  return ids;
}

export function collectVarRefIdsFromValue(
  value: unknown,
  rules: NestedRefRule[] = []
): string[] {
  const ids: string[] = [];
  const visit = (v: unknown, mode?: NestedRefRule["mode"]): void => {
    if (typeof v === "string") {
      if (
        mode === "gather" ||
        (mode === "literal" && !WHOLE_VAR_REF_PATTERN.test(v))
      )
        return;
      ids.push(...extractVarRefIdsFromString(v));
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) visit(item);
      return;
    }
    if (v && typeof v === "object") {
      const record = v as Record<string, unknown>;
      for (const [key, item] of Object.entries(record)) {
        const rule = rules.find(
          (rule) =>
            rule.field === key &&
            Object.entries(rule.when ?? {}).every(
              ([field, expected]) => record[field] === expected
            )
        );
        visit(item, rule?.mode);
      }
    }
  };
  visit(value);
  return ids;
}

/**
 * Write-variant of the ref walk: rewrite every `:var[...]` token through
 * `mapRefId`. A `null` result keeps the raw token verbatim (unknown refs,
 * `__secrets__`), so escaping survives untouched. Rich-text `((ref))`
 * mentions (V1-bridge fields) are deliberately not remapped.
 */
export function remapVarRefsInString(
  text: string,
  mapRefId: (refId: string) => string | null
): string {
  const re = new RegExp(VAR_REF_PATTERN.source, "g");
  return text.replace(re, (raw, refId: string) => {
    const remapped = mapRefId(normalizeVariableRefId(refId));
    return remapped === null ? raw : formatVarRefToken(remapped);
  });
}

export function remapVarRefsInValue(
  value: unknown,
  mapRefId: (refId: string) => string | null
): unknown {
  if (typeof value === "string") return remapVarRefsInString(value, mapRefId);
  if (Array.isArray(value)) {
    return value.map((item) => remapVarRefsInValue(item, mapRefId));
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [
        k,
        remapVarRefsInValue(v, mapRefId),
      ])
    );
  }
  return value;
}

export function remapVarRefsInConfig(
  config: Record<string, unknown>,
  mapRefId: (refId: string) => string | null
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(config).map(([k, v]) => [
      k,
      remapVarRefsInValue(v, mapRefId),
    ])
  );
}

/**
 * `variable_bindings` values are bare `<node>.<output>` refs (no `:var[]`
 * wrapper); keys are slot identifiers and never change.
 */
export function remapVariableBindingRefs(
  bindings: Record<string, string>,
  mapRefId: (refId: string) => string | null
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(bindings).map(([key, ref]) => {
      const remapped = mapRefId(normalizeVariableRefId(ref));
      return [key, remapped ?? ref];
    })
  );
}

export type InlineVariableToken =
  | { kind: "text"; text: string }
  | { kind: "chip"; raw: string; id: string };

export function tokenizeInlineVariableRefs(
  text: string
): InlineVariableToken[] {
  const out: InlineVariableToken[] = [];
  let lastIndex = 0;
  const re = new RegExp(INLINE_VAR_REF_PATTERN.source, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push({ kind: "text", text: text.slice(lastIndex, match.index) });
    }
    const id = normalizeVariableRefId(match[1] ?? match[2] ?? "");
    out.push({ kind: "chip", raw: match[0], id });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    out.push({ kind: "text", text: text.slice(lastIndex) });
  }
  return out;
}

export type VarRefStringPart =
  | { kind: "text"; value: string }
  | { kind: "ref"; refId: string; raw: string };

export function splitStringByVarRefs(text: string): VarRefStringPart[] {
  const parts: VarRefStringPart[] = [];
  let cursor = 0;
  const re = new RegExp(VAR_REF_PATTERN.source, "g");
  for (const match of text.matchAll(re)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      parts.push({ kind: "text", value: text.slice(cursor, start) });
    }
    parts.push({
      kind: "ref",
      refId: normalizeVariableRefId(match[1]),
      raw: match[0],
    });
    cursor = start + match[0].length;
  }
  if (cursor < text.length) {
    parts.push({ kind: "text", value: text.slice(cursor) });
  }
  return parts;
}

export interface NodeBinding {
  refId: string;
  sourceNodeId: string;
  outputName: string;
  sourceLabel?: string;
  sourceImage?: string | null;
  sourceIconName?: string | null;
  sourceColor?: string;
  outputLabel: string;
  dataType: string;
  isList: boolean;
  isSecret: boolean;
  consumedByField: string;
}

export function nodeBindingFromVarRef(
  refId: string,
  consumedByField: string,
  nodes: GraphNodeV2[],
  registry: NodeDefinition[],
  secretsByQualifiedId?: Map<string, SecretLookupEntry>,
  dynamicNodeConfigs?: Map<string, DynamicNodeConfig>
): NodeBinding {
  const normalizedRef = normalizeVariableRefId(refId);
  const parsed = parseVarRefId(normalizedRef);
  if (!parsed) {
    return {
      refId: normalizedRef,
      sourceNodeId: "",
      outputName: "output",
      outputLabel: normalizedRef,
      dataType: "str",
      isList: false,
      isSecret: false,
      consumedByField,
    };
  }

  const nodeDataMap = new Map<string, NodeV2Data>(
    nodes.map((n) => [n.id, n.data])
  );
  const source = resolveBindingTarget(
    parsed.sourceNodeId,
    parsed.outputName,
    nodeDataMap,
    registry,
    secretsByQualifiedId,
    dynamicNodeConfigs
  );

  return {
    refId: normalizedRef,
    sourceNodeId: parsed.sourceNodeId,
    outputName: parsed.outputName,
    sourceLabel: source.sourceName,
    sourceImage: source.sourceImage,
    sourceIconName: source.sourceIconName,
    sourceColor: source.sourceColor,
    outputLabel: source.sourceOutput,
    dataType: source.sourceDataType ?? "str",
    isList: source.sourceIsList,
    isSecret: source.isSecret,
    consumedByField,
  };
}

export function collectNodeBindingsFromConfig(
  config: Record<string, unknown>,
  nodes: GraphNodeV2[],
  registry: NodeDefinition[],
  secretsByQualifiedId?: Map<string, SecretLookupEntry>,
  dynamicNodeConfigs?: Map<string, DynamicNodeConfig>
): NodeBinding[] {
  const seen = new Map<string, NodeBinding>();
  const visit = (value: unknown, fieldKey: string): void => {
    if (typeof value === "string") {
      for (const refId of extractVarRefIdsFromString(value)) {
        if (seen.has(refId)) continue;
        seen.set(
          refId,
          nodeBindingFromVarRef(
            refId,
            fieldKey,
            nodes,
            registry,
            secretsByQualifiedId,
            dynamicNodeConfigs
          )
        );
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) visit(item, fieldKey);
      return;
    }
    if (value && typeof value === "object") {
      for (const [k, v] of Object.entries(value)) visit(v, fieldKey || k);
    }
  };

  for (const [key, value] of Object.entries(config)) visit(value, key);
  return Array.from(seen.values());
}

/**
 * Bindings a frozen definition node consumes: inline `:var[...]` refs in its
 * config UNIONED with the legacy `variable_bindings` map (bare `node.output`
 * values, no inline token) — the engine resolves both, so the run form must
 * too. Deduped by refId, preferring the inline occurrence.
 */
export function collectNodeBindingsFromDefinitionNode(
  defNode: NodeDefinitionV2,
  nodes: GraphNodeV2[],
  registry: NodeDefinition[],
  secretsByQualifiedId?: Map<string, SecretLookupEntry>,
  dynamicNodeConfigs?: Map<string, DynamicNodeConfig>
): NodeBinding[] {
  const inline = collectNodeBindingsFromConfig(
    defNode.node_config,
    nodes,
    registry,
    secretsByQualifiedId,
    dynamicNodeConfigs
  );
  const seen = new Map<string, NodeBinding>(inline.map((b) => [b.refId, b]));
  for (const [fieldKey, rawRef] of Object.entries(
    defNode.variable_bindings ?? {}
  )) {
    if (typeof rawRef !== "string") continue;
    const refId = normalizeVariableRefId(rawRef);
    if (!parseVarRefId(refId) || seen.has(refId)) continue;
    seen.set(
      refId,
      nodeBindingFromVarRef(
        refId,
        fieldKey,
        nodes,
        registry,
        secretsByQualifiedId,
        dynamicNodeConfigs
      )
    );
  }
  return Array.from(seen.values());
}
